import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, Building2, Users, Inbox } from "lucide-react";
import { useAuth } from "@/admin/AuthContext";
import AdminLayout from "@/admin/AdminLayout";

export default function AdminDashboard() {
  const { user } = useAuth();

  if (user?.role === "visitor") {
    return (
      <AdminLayout title="Sem permissão de acesso">
        <div
          data-testid="admin-no-access"
          className="rounded-2xl border border-[#FA9F22]/30 bg-[#FA9F22]/8 p-8"
        >
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#FA9F22]/15 text-[#FA9F22]">
            <AlertTriangle size={20} />
          </div>
          <h2 className="mt-5 font-display text-xl font-semibold text-[#1B1D1D]">
            Sua conta ainda não está vinculada a um perfil.
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-[#51565F] max-w-2xl">
            O acesso ao painel é liberado quando o seu e-mail Google corresponde
            ao contato cadastrado em um perfil de parceiro do diretório — ou
            quando faz parte da equipe RSM. Logado como{" "}
            <span className="font-mono text-[#1B1D1D]">{user.email}</span>.
          </p>
          <p className="mt-4 text-sm text-[#51565F]">
            Se você acredita que isto é um engano, fale com o time RSM citando
            esse e-mail para fazermos o vínculo.
          </p>
        </div>
      </AdminLayout>
    );
  }

  if (user?.role === "partner") {
    return (
      <AdminLayout
        title={`Olá, ${user.name?.split(" ")[0] || "parceiro"}.`}
        subtitle="Mantenha o seu perfil no diretório sempre atualizado."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ActionCard
            to="/admin/perfil"
            testid="card-partner-profile"
            icon={Building2}
            color="#7E20FF"
            title="Editar meu perfil"
            desc="Tagline, descrição, especialidades, contato e logo aparecem no diretório público."
          />
          <ActionCard
            to={`/politics/parceiros/${user.partner_slug}`}
            external
            testid="card-public-profile"
            icon={Users}
            color="#208BFF"
            title="Ver perfil público"
            desc="Visualize como o seu perfil é exibido no diretório /politics/parceiros."
          />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Painel RSM"
      subtitle="Gerencie o diretório de parceiros e veja os pools de leads."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ActionCard
          to="/admin/parceiros"
          testid="card-partners"
          icon={Building2}
          color="#208BFF"
          title="Parceiros"
          desc="Liste, crie, edite e remova perfis do diretório /politics/parceiros."
        />
        <ActionCard
          to="/admin/leads"
          testid="card-leads"
          icon={Inbox}
          color="#7E20FF"
          title="Leads consolidados"
          desc="Visualize todos os pools de leads (Politics, Agências, SaaS, Campanhas, Network)."
        />
        <ActionCard
          to="/politics/parceiros"
          external
          testid="card-directory"
          icon={Users}
          color="#3ED383"
          title="Diretório público"
          desc="Conferir o diretório do jeito que o público vê."
        />
      </div>
    </AdminLayout>
  );
}

function ActionCard({ to, icon: Icon, title, desc, color, testid, external }) {
  const inner = (
    <>
      <div
        className="grid h-12 w-12 place-items-center rounded-xl"
        style={{ backgroundColor: `${color}15`, color }}
      >
        <Icon size={22} />
      </div>
      <h3 className="mt-5 font-display text-lg font-semibold text-[#1B1D1D]">
        {title}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-[#51565F]">{desc}</p>
      <span
        className="mt-4 inline-flex items-center gap-1 text-xs font-semibold"
        style={{ color }}
      >
        Abrir <ArrowRight size={12} />
      </span>
    </>
  );
  const cls =
    "group flex flex-col rounded-2xl border border-[#1B1D1D]/8 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md";
  if (external) {
    return (
      <a href={to} target="_blank" rel="noreferrer" data-testid={testid} className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testid} className={cls}>
      {inner}
    </Link>
  );
}
